/*
 * Copyright (c) 2018-2019, F5 Networks, Inc. All rights reserved.
 *
 * No part of this software may be reproduced or transmitted in any
 * form or by any means, electronic or mechanical, for any purpose,
 * without express written permission of F5 Networks, Inc.
 */
/// <reference types="cache-fm" />
import App from 'apmui/App';
import EventTags from 'apmui/EventTags';
import IDialogConfiguration, {
    DialogClass,
    DialogPriority,
    DialogType,
    CommonDialogAction,
} from 'apmui/components/dialog/Model';
import * as Dialog from 'apmui/components/dialog/DialogHelper';
import { IWebtopTimeoutConfig, IWebtopTimeoutDialogConfig } from './Model';
import bind from 'apmui/decorators/bind';

import 'apmui/components/dialog/Controller';

const app = App.get();

class WebtopSessionTimeout {
    max: number;
    remainingPeriod: number;
    interval: number;

    start() {
        window.F5_ST_start(this.displayRemainingPeriod, this.displayDialog, this.dismissDialog);
    }

    // callback to show timeout dialog
    @bind
    displayDialog(max: number, remainingPeriod: number, stayCB: () => void, logoutCB: () => void) {
        this.max = max;
        this.remainingPeriod = remainingPeriod;
        app.showModal(DialogType.SESSION_TIMEOUT)
            .then(() => stayCB())
            .catch(() => logoutCB());
        //
        this.interval = window.setInterval(() => {
            app.dispatch(EventTags.REMAINING_TIME_UPDATE);
        }, 1000);
    }

    // callback to show the time remained
    @bind
    displayRemainingPeriod(max: number, remainingPeriod: number) {
        this.max = max;
        this.remainingPeriod = remainingPeriod;
        // remaining time will be refreshed by updateDialog()
    }

    // callback to close timeout dialog
    @bind
    dismissDialog() {
        // close dialog if it has not been closed
        app.closeModal({ dialog: DialogClass.TIMEOUT, resolve: true });
        // clean the timer
        if (this.interval) {
            clearInterval(this.interval);
            this.interval = 0;
        }
    }

    // refresh remaining time
    updateDialog(webtopTimeout: IWebtopTimeoutConfig, dialog: IDialogConfiguration) {
        if (this.remainingPeriod > 0) {
            this.remainingPeriod--;
            dialog.body = this.fillDialogBody(this.max ? webtopTimeout.dialogMax : webtopTimeout.dialog);
        }
    }

    fillDialogBody(strings: IWebtopTimeoutDialogConfig) {
        return `${strings.messagePrefix}<br>
                ${strings.messageSecondsPrefix} ${Math.round(this.remainingPeriod)}
                    ${strings.messageSecondsPostfix}<br>
                ${strings.messagePostfix}`;
    }

    fillDialogConfiguration(strings: IWebtopTimeoutDialogConfig): IDialogConfiguration {
        return {
            id: DialogType.SESSION_TIMEOUT,
            class: DialogClass.TIMEOUT,
            title: strings.header,
            body: this.fillDialogBody(strings),
            entries: [
                {
                    id: CommonDialogAction.OK,
                    style: 'submit',
                    caption: strings.stayConnectedCaption,
                },
                {
                    id: CommonDialogAction.CANCEL,
                    caption: strings.disconnectCaption,
                },
            ],
            priority: DialogPriority.HIGH_PRIORITY,
        };
    }

    getDialogConfiguration(webtopTimeout: IWebtopTimeoutConfig): IDialogConfiguration {
        return this.fillDialogConfiguration(this.max ? webtopTimeout.dialogMax : webtopTimeout.dialog);
    }
}

export const webtopSessionTimeout = new WebtopSessionTimeout();

app.subscribe(EventTags.DIALOG_OPEN, ({ detail }, store) => {
    if (detail.dialog === DialogType.SESSION_TIMEOUT && store.pageType === 'webtop') {
        detail.dialog = webtopSessionTimeout.getDialogConfiguration(store.webtop.timeout);
    }
});

app.subscribe(EventTags.REMAINING_TIME_UPDATE, (_, store) => {
    if (store.pageType === 'webtop' && webtopSessionTimeout.interval !== 0) {
        const dialog = Dialog.get(store, DialogType.SESSION_TIMEOUT);
        if (dialog) {
            webtopSessionTimeout.updateDialog(store.webtop.timeout, dialog);
        }
    }
});
